import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Alert, CircularProgress } from "@mui/material";
import { usePlacesWidget } from "react-google-autocomplete";
import PublicShell from "./components/PublicShell";
import PhoneVerify from "./components/PhoneVerify";
import VehicleDetailsForm from "./components/VehicleDetailsForm";
import PassActiveCard from "./components/PassActiveCard";
import { Check } from "./components/icons";
import { apiErrorMessage, buildVehicleFormData, guestApi, readSession, unitLabel, writeSession } from "./publicVehicleApi";
import styles from "./publicvehicle.module.css";

const SCOPE = "guest";

const CLOSED_SCREENS = {
  EXPIRED: { severity: "warning", text: "This invitation has expired. Please ask your host to send you a new link." },
  PAYMENT_EXPIRED: { severity: "warning", text: "The payment link for this pass has expired. Please ask your host to invite you again." },
  REJECTED: { severity: "warning", text: "Your vehicle was not approved for guest parking." },
  CANCELLED: { severity: "info", text: "This invitation was cancelled. Please ask your host for a new one." },
};

const durationText = (hours) => (hours >= 48 ? `Valid for ${Math.round(hours / 24)} days` : `Valid for ${hours} hours`);

// Guest submitted, waiting for the property manager
const PendingScreen = ({ invite }) => (<>
  <section className={`${styles.card} ${styles.centerText}`}>
    <div className={styles.successCircle}><Check /></div>
    <h2 className={styles.cardTitle}>Thanks, {invite.guest_fname}</h2>
    <p className={styles.cardText}>Your vehicle details were sent to the property manager for approval. {invite.host_name || "Your host"} will see the status in their app, and we'll text you a link to pay the parking fee once it's approved.</p>
  </section>
  <section className={styles.card}>
    <p className={styles.sectionLabel}>SUMMARY</p>
    <dl className={styles.summaryList}>
      <div><dt>Status</dt><dd><span className={`${styles.chip} ${styles.chip_pending}`}>Pending approval</span></dd></div>
      {invite.property_address && <div><dt>Address</dt><dd>{invite.property_address}</dd></div>}
      <div><dt>Vehicle</dt><dd>{[invite.vehicle?.make, invite.vehicle?.model].filter(Boolean).join(" ")} · {invite.vehicle?.license}</dd></div>
      <div><dt>Photos</dt><dd>{invite.vehicle?.photo_count || 0} uploaded</dd></div>
    </dl>
  </section>
  <p className={styles.lockNote}>You can close this page.</p>
</>);

const PaymentForm = ({ token, invite, onError }) => {
  const passTypes = invite.pass_types || [];
  const [passType, setPassType] = useState(() => (passTypes.find((p) => p.key === "monthly") || passTypes[0] || {}).key || "");
  const [fullName, setFullName] = useState(`${invite.guest_fname || ""} ${invite.guest_lname || ""}`.trim());
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [address2, setAddress2] = useState("");
  // Filled from the Google Places suggestion (US addresses only); cleared if the street line is retyped
  const [place, setPlace] = useState({ city: "", state: "", zip: "" });

  // Same Places setup as the expense payee form (react-google-autocomplete)
  const { ref: addressRef } = usePlacesWidget({
    apiKey: process.env.REACT_APP_MAP_API_KEY,
    options: {
      types: ["address"],
      componentRestrictions: { country: "us" },
      fields: ["address_components"],
    },
    onPlaceSelected: (selected) => {
      let street = "";
      let city = "";
      let state = "";
      let zip = "";

      for (const component of selected?.address_components || []) {
        switch (component.types[0]) {
          case "street_number":
            street = component.long_name;
            break;
          case "route":
            street = `${street} ${component.short_name}`.trim();
            break;
          case "postal_code":
            zip = component.long_name;
            break;
          case "locality":
          case "sublocality_level_1":
          case "administrative_area_level_3":
            city = component.long_name;
            break;
          case "administrative_area_level_1":
            state = component.short_name;
            break;
          default:
            break;
        }
      }

      setAddress(street);
      setPlace({ city, state, zip });
      setErrors((prev) => ({ ...prev, address: undefined }));
    },
  });
  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const selected = passTypes.find((p) => p.key === passType);
  const bestValueKey = passTypes.length > 1 ? passTypes.reduce((best, p) => (p.duration_hours > best.duration_hours ? p : best), passTypes[0]).key : "";

  const pay = async (e) => {
    e.preventDefault();
    const next = {};
    if (fullName.trim() === "") next.fullName = "Enter your full name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) next.email = "Enter a valid email address";
    if (address.trim() === "") next.address = "Enter your address";
    if (!agree) next.agree = "Please agree to the terms and conditions";
    setErrors(next);
    if (Object.keys(next).length) return;

    setBusy(true);
    setError("");
    try {
      const response = await guestApi.checkout(token, { pass_type: passType, full_name: fullName.trim(), email: email.trim(), address: address.trim(), address2: address2.trim(), city: place.city, state: place.state, zip: place.zip, agree: 1 });
      if (!response?.data?.checkout_url) throw new Error("Missing checkout url");
      window.location.href = response.data.checkout_url;
    } catch (err) {
      console.error("[GuestVehicle] Failed to start checkout", err);
      setError(apiErrorMessage(err, "Unable to start the payment. Please try again."));
      if (err?.response?.status === 409 || err?.response?.status === 410) onError();
      setBusy(false);
    }
  };

  return (<form className={styles.form} onSubmit={pay} noValidate>
    {invite.pass?.payment_failed && <Alert severity="error">Your last payment didn't go through. Please check your card details and try again.</Alert>}
    <section className={styles.feeCard}>
      <span className={styles.muted}>{invite.property_name} Guest Parking Fee</span>
      <span className={styles.feeAmount}>${Number(selected?.amount || 0).toFixed(2)}</span>
      <span className={styles.muted}>Fee</span>
    </section>

    <section className={styles.card}>
      <p className={styles.sectionLabel}>PAYMENT INFORMATION</p>
      <div role="radiogroup" aria-label="Pass type" className={styles.passOptions}>
        {passTypes.map((option) => (<button
          type="button"
          role="radio"
          aria-checked={passType === option.key}
          key={option.key}
          disabled={busy}
          className={passType === option.key ? `${styles.passOption} ${styles.passOptionOn}` : styles.passOption}
          onClick={() => setPassType(option.key)}
        >
          {option.key === bestValueKey && <span className={styles.bestValue}>Best Value</span>}
          <span className={styles.passPrice}>${Number(option.amount) % 1 === 0 ? Number(option.amount) : Number(option.amount).toFixed(2)}</span>
          <span className={styles.passLabel}>{option.label}</span>
          <span className={styles.muted}>{durationText(option.duration_hours)}</span>
        </button>))}
      </div>
      <dl className={styles.summaryList}>
        <div><dt>Vehicle</dt><dd>{[invite.vehicle?.make, invite.vehicle?.model].filter(Boolean).join(" ")} <span className={styles.plate}>{invite.vehicle?.license}</span></dd></div>
      </dl>
    </section>

    <section className={styles.card}>
      <p className={styles.sectionLabel}>BILLING CONTACT &amp; ADDRESS</p>
      <div className={styles.field}>
        <label htmlFor="pay-name" className={styles.label}>Full name*</label>
        <input id="pay-name" className={`${styles.input} ${errors.fullName ? styles.inputError : ""}`} value={fullName} maxLength={100} disabled={busy} onChange={(e) => setFullName(e.target.value)} autoComplete="name" />
        {errors.fullName && <span className={styles.fieldError}>{errors.fullName}</span>}
      </div>
      <div className={styles.field}>
        <label htmlFor="pay-email" className={styles.label}>Email*</label>
        <input id="pay-email" type="email" className={`${styles.input} ${errors.email ? styles.inputError : ""}`} value={email} maxLength={100} disabled={busy} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="example@gmail.com" />
        {errors.email && <span className={styles.fieldError}>{errors.email}</span>}
      </div>
      <div className={styles.field}>
        <label htmlFor="pay-address" className={styles.label}>Address*</label>
        <input ref={addressRef} id="pay-address" className={`${styles.input} ${errors.address ? styles.inputError : ""}`} value={address} maxLength={200} disabled={busy} onChange={(e) => { setAddress(e.target.value); setPlace({ city: "", state: "", zip: "" }); }} autoComplete="off" placeholder="Start typing your address" />
        {(place.city || place.state || place.zip) && <span className={styles.hint}>{[place.city, [place.state, place.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</span>}
        {errors.address && <span className={styles.fieldError}>{errors.address}</span>}
      </div>
      <div className={styles.field}>
        <label htmlFor="pay-address2" className={styles.label}>Address line 2</label>
        <input id="pay-address2" className={styles.input} value={address2} maxLength={100} disabled={busy} onChange={(e) => setAddress2(e.target.value)} autoComplete="address-line2" />
      </div>
    </section>

    <section className={styles.card}>
      <p className={styles.sectionLabel}>TERMS AND CONDITIONS</p>
      <p className={styles.termsText}>By proceeding with this payment, you confirm that the information provided is accurate and authorize {invite.property_name || "the property"} to charge the selected parking fee. Your parking pass starts when payment is received, is valid only for the selected duration and vehicle, and must be used in accordance with the property's parking rules. Parking fees are non-refundable once the pass has been issued, except where required by law. {invite.property_name || "The property"} is not responsible for any loss, theft, or damage to vehicles or personal belongings.</p>
      <label className={styles.checkRow}>
        <input type="checkbox" checked={agree} disabled={busy} onChange={(e) => setAgree(e.target.checked)} />
        I agree to the terms and conditions
      </label>
      {errors.agree && <span className={styles.fieldError}>{errors.agree}</span>}
    </section>

    {error !== "" && <Alert severity="error">{error}</Alert>}
    <button type="submit" className={`${styles.primaryButton} ${styles.fullWidth}`} disabled={busy || !passType}>{busy ? "REDIRECTING..." : `PAY $${Number(selected?.amount || 0).toFixed(2)}`}</button>
  </form>);
};

const GuestVehicle = () => {
  const { token } = useParams();
  // ?edit=1 is the edit link the host sends from the app: edit-only flow, never the payment screen
  // (the pay link is texted when the property manager approves)
  const editMode = new URLSearchParams(useLocation().search).get("edit") === "1";
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [session, setSession] = useState(() => readSession(SCOPE, token));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Message for the edit-done screen (after saving or cancelling)
  const [editDone, setEditDone] = useState("");

  const load = useCallback(async (sessionToken) => {
    setLoadError("");
    try {
      const response = await guestApi.get(token, sessionToken || readSession(SCOPE, token));
      setInvite(response?.data?.data || null);
    } catch (err) {
      console.error("[GuestVehicle] Failed to load invitation", err);
      setLoadError(apiErrorMessage(err, "Unable to load this invitation. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const sendCode = async () => {
    try {
      const response = await guestApi.sendOtp(token);
      return response?.data?.otp_token || "";
    } catch (err) {
      console.error("[GuestVehicle] Failed to send code", err);
      if (err?.response?.status === 409 || err?.response?.status === 410) load();
      throw apiErrorMessage(err, "Unable to send the code. Please try again.");
    }
  };

  const verifyCode = async (code, otpToken) => {
    try {
      const response = await guestApi.verifyOtp(token, { code, otp_token: otpToken });
      const value = response?.data?.session_token || "";
      writeSession(SCOPE, token, value, response?.data?.expires_in || 3600);
      // Reload first so the edit form opens with the full phone number
      if (editMode) await load(value);
      setSession(value);
    } catch (err) {
      console.error("[GuestVehicle] Failed to verify code", err);
      throw apiErrorMessage(err, "Unable to verify the code. Please try again.");
    }
  };

  const submitDetails = async (values, photos) => {
    setBusy(true);
    setError("");
    try {
      const response = await guestApi.submitDetails(token, buildVehicleFormData(values, photos, { session }));
      writeSession(SCOPE, token, "", 0);
      setInvite(response?.data?.data || null);
      if (!response?.data?.data) await load();
    } catch (err) {
      console.error("[GuestVehicle] Failed to save vehicle details", err);
      if (err?.response?.data?.reverify) {
        writeSession(SCOPE, token, "", 0);
        setSession("");
      }
      if (err?.response?.status === 409 || err?.response?.status === 410) load();
      setError(apiErrorMessage(err, "Unable to save your vehicle details. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  // Edit link from the host: the PENDING submission is replaced; for an approved vehicle the changes go to
  // the property manager and the current pass keeps working until they're approved
  const editDetails = async (values, photos, keptIds) => {
    setBusy(true);
    setError("");
    try {
      const response = await guestApi.editDetails(token, buildVehicleFormData(values, photos, {
        session,
        // Kept current photos + new ones make the final set (only when the current photos were shown)
        photos_edited: Array.isArray(invite?.photos) ? 1 : undefined,
        keep_photos: Array.isArray(invite?.photos) ? keptIds : undefined,
        guest_fname: values.guest_fname,
        guest_lname: values.guest_lname,
        guest_phone: values.guest_phone,
      }));
      writeSession(SCOPE, token, "", 0);
      setSession("");
      setEditDone(response?.data?.message || "Your changes were saved.");
      setInvite(response?.data?.data || null);
      if (!response?.data?.data) await load();
    } catch (err) {
      console.error("[GuestVehicle] Failed to save vehicle changes", err);
      if (err?.response?.data?.reverify) {
        writeSession(SCOPE, token, "", 0);
        setSession("");
      }
      // The edit link expired or was used meanwhile: reload shows the "link expired" screen
      if (err?.response?.status === 409 || err?.response?.status === 410) load();
      setError(apiErrorMessage(err, "Unable to save your changes. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  // Shown on the pending / pay / pass screens (regular link): changes from an edit link waiting for review
  const editBar = invite?.has_pending_update
    ? <Alert severity="info" className={styles.block}>Your vehicle changes are waiting for the property manager. Your current pass keeps working until then.</Alert>
    : null;

  const shellProps = {
    propertyName: invite?.property_name,
    propertyAddress: invite?.property_address,
    propertyPhone: invite?.property_phone,
  };
  const invitedBy = invite ? `Invited by ${invite.host_name || "your host"}${invite.apt_no ? ` · ${unitLabel(invite.apt_no)}` : ""}` : "";

  if (loading) {
    return (<PublicShell title="Guest parking"><div className={styles.center}><CircularProgress size={30} /></div></PublicShell>);
  }

  if (!invite) {
    return (<PublicShell title="Guest parking">
      <Alert severity="error">{loadError}</Alert>
      <div className={styles.actions}><button className={styles.primaryButton} onClick={() => { setLoading(true); load(); }}>TRY AGAIN</button></div>
    </PublicShell>);
  }

  // Edit link: done (saved or cancelled), closed (expired / used) or the edit flow. Status screens never render.
  if (editMode && editDone !== "") {
    return (<PublicShell title="Edit your vehicle" context={invitedBy} {...shellProps}>
      <section className={`${styles.card} ${styles.centerText}`}>
        <div className={styles.successCircle}><Check /></div>
        <h2 className={styles.cardTitle}>Thanks, {invite.guest_fname}</h2>
        <p className={styles.cardText}>{editDone}</p>
      </section>
      <section className={styles.card}>
        <p className={styles.sectionLabel}>YOUR VEHICLE</p>
        <dl className={styles.summaryList}>
          <div><dt>Vehicle</dt><dd>{[invite.vehicle?.color, invite.vehicle?.make, invite.vehicle?.model].filter(Boolean).join(" ")} <span className={styles.plate}>{invite.vehicle?.license}</span></dd></div>
          <div><dt>Photos</dt><dd>{invite.vehicle?.photo_count || 0} on file</dd></div>
        </dl>
      </section>
      <p className={styles.lockNote}>You can close this page.</p>
    </PublicShell>);
  }

  if (editMode && !invite.can_edit) {
    return (<PublicShell title="Edit your vehicle" context={invitedBy} {...shellProps}>
      <Alert severity="info">
        {invite.has_pending_update
          ? "Your changes were sent to the property manager. You'll get a text once they're reviewed."
          : "This edit link has expired or was already used. Please ask your host to send a new one."}
      </Alert>
    </PublicShell>);
  }

  if (editMode) {
    return (<PublicShell
      title="Edit your vehicle"
      context={invitedBy}
      {...shellProps}
      steps={[{ label: session ? "Phone verified" : "Verify phone", state: session ? "done" : "active" }, { label: "Vehicle details", state: session ? "active" : "todo" }]}
    >
      {!session && <PhoneVerify greeting={`Hi ${invite.guest_fname}, verify your number`} phoneHint={invite.phone_hint} sendCode={sendCode} verifyCode={verifyCode} />}
      {session && (<>
        {invite.status === "REJECTED"
          ? <Alert severity="error" className={styles.block}>Your vehicle was not approved{invite.review_note ? `: ${invite.review_note}` : ""}. Please fix the details below and submit again.</Alert>
          : invite.status !== "PENDING" && <Alert severity="info" className={styles.block}>Your changes will be sent to the property manager for approval. Your current pass keeps working until then.</Alert>}
        {error !== "" && <Alert severity="error" className={styles.block}>{error}</Alert>}
        <VehicleDetailsForm
          key={`${invite.guest_phone ? "with-phone" : "no-phone"}-${Array.isArray(invite.photos) ? invite.photos.length : "none"}`}
          defaultValues={invite.vehicle}
          existingPhotos={invite.photos}
          guestFields={{ fname: invite.guest_fname, lname: invite.guest_lname, phone: invite.guest_phone }}
          requirePhotos={false}
          minPhotos={invite.min_photos || 1}
          maxPhotos={invite.max_photos || 6}
          submitting={busy}
          submitLabel={invite.status === "REJECTED" ? "SUBMIT AGAIN" : "SAVE CHANGES"}
          onSubmit={editDetails}
          onCancel={() => { setError(""); setEditDone("No changes were made to your vehicle."); }}
        />
      </>)}
    </PublicShell>);
  }

  if (invite.status === "ACTIVE" || invite.status === "PASS_ENDED") {
    return (<PublicShell title="Guest parking pass" {...shellProps}>
      {editBar}
      <PassActiveCard data={{ ...invite, guest_name: `${invite.guest_fname} ${invite.guest_lname}`.trim() }} />
    </PublicShell>);
  }

  if (invite.status === "AWAITING_PAYMENT") {
    return (<PublicShell title="Pay parking fee" context={`Vehicle ${invite.vehicle?.license} approved for guest parking`} {...shellProps}>
      {editBar}
      <PaymentForm token={token} invite={invite} onError={load} />
    </PublicShell>);
  }

  if (invite.status === "PENDING") {
    return (<PublicShell title="Guest vehicle submitted" context={invitedBy} {...shellProps}>
      {editBar}
      <PendingScreen invite={invite} />
    </PublicShell>);
  }

  const closed = CLOSED_SCREENS[invite.status];
  if (closed) {
    return (<PublicShell title="Guest parking" context={invitedBy} {...shellProps}>
      <Alert severity={closed.severity}>{closed.text}{invite.status === "REJECTED" && invite.review_note ? ` Reason: ${invite.review_note}` : ""}</Alert>
    </PublicShell>);
  }

  // INVITED: verify phone, then vehicle details
  return (<PublicShell
    title="Add your vehicle for your visit"
    context={invitedBy}
    {...shellProps}
    steps={[{ label: session ? "Phone verified" : "Verify phone", state: session ? "done" : "active" }, { label: "Vehicle details", state: session ? "active" : "todo" }]}
  >
    {!session && <PhoneVerify greeting={`Hi ${invite.guest_fname}, verify your number`} phoneHint={invite.phone_hint} sendCode={sendCode} verifyCode={verifyCode} />}
    {session && (<>
      {error !== "" && <Alert severity="error" className={styles.block}>{error}</Alert>}
      <VehicleDetailsForm requirePhotos minPhotos={invite.min_photos || 1} maxPhotos={invite.max_photos || 6} submitting={busy} submitLabel="SUBMIT VEHICLE" onSubmit={submitDetails} />
    </>)}
  </PublicShell>);
};

export default GuestVehicle;
